/**
 * <cookie-banner> (sections/cookie-banner.liquid).
 *
 * A thin set of controls over Shopify's Customer Privacy API. Everything
 * that could be got wrong by guessing is delegated to it:
 *
 *   shouldShowBanner()   whether this visitor must be asked at all. It
 *                        already accounts for their region, the store's
 *                        regional settings and any consent given earlier.
 *                        Second-guessing it means nagging a shopper who
 *                        never needed asking, or tracking one who did.
 *   setTrackingConsent() records the answer, in its own cookie, which is
 *                        the actual record of consent.
 *
 * The localStorage stamp below is NOT the consent record. It exists only so
 * the delay and the "ask again after" window survive a page change; the API
 * is the source of truth, and a visitor who clears local storage but keeps
 * their consent cookie is still not asked again.
 *
 * WHEN THE API IS NOT THERE
 *
 * loadFeatures can fail — an ad blocker, a network hiccup, a preview
 * context. The banner then falls back to the localStorage stamp alone: it
 * asks, and it remembers the answer for the configured window, but it
 * cannot write real consent. That is the honest degradation. Showing
 * nothing would silently drop the question; pretending to record consent
 * would be worse than either.
 *
 * ESC DOES NOTHING, DELIBERATELY
 *
 * There is no keydown handler here and that is the point. Dismissing this
 * by keyboard would have to mean accepting or declining on the visitor's
 * behalf, and consent has to be given rather than inferred. The banner is
 * also not modal, so the page stays usable while it is open — nobody is
 * held hostage until they answer.
 */
if (!customElements.get('cookie-banner')) {
  var DAY_MS = 24 * 60 * 60 * 1000;

  class CookieBanner extends HTMLElement {
    connectedCallback() {
      this.region = this.querySelector('[data-cookie-region]');
      if (!this.region) return;

      this.panel = this.querySelector('[data-cookie-panel]');
      this.preferencesButton = this.querySelector('[data-cookie-preferences]');
      this.analytics = this.querySelector('[data-cookie-analytics]');
      this.marketing = this.querySelector('[data-cookie-marketing]');

      this.sectionId = this.dataset.sectionId || '';
      this.storageKey = this.dataset.storageKey || 'glide:cookie-consent';
      this.rememberDays = Number(this.dataset.rememberDays);
      if (!isFinite(this.rememberDays) || this.rememberDays < 1) this.rememberDays = 180;
      this.delayMs = (Number(this.dataset.delay) || 0) * 1000;
      this.timer = null;

      this.onClick = this.onClick.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.addEventListener('click', this.onClick);

      /* In the theme editor nothing appears by itself: a consent banner
         that returns after every setting change sits on top of the footer
         the merchant is trying to edit. It shows while this section is
         selected instead, which is how they preview it deliberately. */
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        return;
      }

      this.loadPrivacyApi();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    }

    /* ---------------------------------------------------------------- */
    /* The API                                                           */
    /* ---------------------------------------------------------------- */
    loadPrivacyApi() {
      var self = this;
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        this.decideWithoutApi();
        return;
      }

      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (error || !window.Shopify.customerPrivacy) {
            self.decideWithoutApi();
            return;
          }
          self.decideWithApi();
        }
      );
    }

    decideWithApi() {
      var privacy = window.Shopify.customerPrivacy;
      var shouldShow;
      try {
        shouldShow = privacy.shouldShowBanner();
      } catch (error) {
        /* An API that is present but not ready is not an answer either
           way, so fall back rather than assume. */
        this.decideWithoutApi();
        return;
      }
      if (!shouldShow) return;
      if (this.isSuppressed()) return;
      this.scheduleShow();
    }

    decideWithoutApi() {
      if (this.isSuppressed()) return;
      this.scheduleShow();
    }

    /* ---------------------------------------------------------------- */
    /* Showing                                                           */
    /* ---------------------------------------------------------------- */
    scheduleShow() {
      if (this.delayMs <= 0) {
        this.show();
        return;
      }
      var self = this;
      this.timer = setTimeout(function () {
        self.show();
      }, this.delayMs);
    }

    show() {
      if (!this.hidden) return;
      this.hidden = false;
      /* The caret follows the banner in, because a screen reader user who
         is never sent there has no way of knowing a question just
         appeared. The region is focused rather than the Accept button, so
         its label and message are announced before the choices. */
      this.region.focus();
    }

    hide() {
      this.hidden = true;
      clearTimeout(this.timer);
    }

    /* ---------------------------------------------------------------- */
    /* Answering                                                         */
    /* ---------------------------------------------------------------- */
    onClick(event) {
      if (event.target.closest('[data-cookie-accept]')) {
        this.record({ analytics: true, marketing: true });
        return;
      }
      if (event.target.closest('[data-cookie-decline]')) {
        this.record({ analytics: false, marketing: false });
        return;
      }
      if (event.target.closest('[data-cookie-save]')) {
        this.record({
          analytics: Boolean(this.analytics && this.analytics.checked),
          marketing: Boolean(this.marketing && this.marketing.checked),
        });
        return;
      }
      if (event.target.closest('[data-cookie-preferences]')) this.togglePanel();
    }

    togglePanel() {
      if (!this.panel || !this.preferencesButton) return;
      var open = this.panel.hidden;
      this.panel.hidden = !open;
      this.preferencesButton.setAttribute('aria-expanded', String(open));
      if (open && this.analytics) this.analytics.focus();
    }

    /* Two checkboxes, four consent flags. `preferences` follows analytics —
       both mean "remember something about this visit" — and `sale_of_data`
       follows marketing, which is the pairing Shopify's own documentation
       uses. Asking a shopper four questions where two will do is how a
       consent panel stops being read. */
    record(choice) {
      var consent = {
        analytics: choice.analytics,
        marketing: choice.marketing,
        preferences: choice.analytics,
        sale_of_data: choice.marketing,
      };

      this.writeStore();
      this.hide();

      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.setTrackingConsent !== 'function') return;
      try {
        privacy.setTrackingConsent(consent, function () {});
      } catch (error) {
        /* The banner is already gone and the local stamp is already
           written, so the visitor is not asked again this window. Nothing
           useful is left to do here but not throw. */
      }
    }

    /* ---------------------------------------------------------------- */
    /* The local stamp                                                   */
    /* ---------------------------------------------------------------- */
    /* Wrapped, because Safari in private mode and any browser with site
       data blocked throw on the property access itself, not merely on the
       write — and an exception here would leave the banner either
       permanently silent or permanently returning. */
    isSuppressed() {
      var last = Number(this.readStore());
      if (!last) return false;
      return Date.now() - last < this.rememberDays * DAY_MS;
    }

    readStore() {
      try {
        return window.localStorage.getItem(this.storageKey);
      } catch (error) {
        return null;
      }
    }

    writeStore() {
      try {
        window.localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (error) {
        /* The API's own cookie is the real record; this stamp only drives
           the ask-again window, so losing it costs at most one repeat. */
      }
    }

    /* ---------------------------------------------------------------- */
    /* Theme editor                                                      */
    /* ---------------------------------------------------------------- */
    onSectionSelect(event) {
      if (String(event.detail.sectionId) !== String(this.sectionId)) return;
      this.hidden = false;
    }

    onSectionDeselect(event) {
      if (String(event.detail.sectionId) !== String(this.sectionId)) return;
      this.hidden = true;
    }
  }

  customElements.define('cookie-banner', CookieBanner);
}
